import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Plane, MapPin, Clock, Shield, Users, Star, CheckCircle2, Calendar, CreditCard, Building2,
  Heart, Briefcase, ArrowLeftRight, Search, MessageCircle, Phone, PartyPopper, Ship, ChevronRight, Anchor
} from "lucide-react";
import axios from "axios";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PublicHeader from "../components/PublicHeader";
import SeoFooter from "../components/SeoFooter";
import { locations, cityLinks } from "../data/locationsData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/8133e222e89d8b736b990c9df71d978bc2c481bdc540061730e97539c8cc1a5f.jpeg";
const MINIBUS_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/26c02035ac26d02ccf15b201aa8d76d5a74fd1fecfec00efb2ae24df30948a9a.jpeg";
const HENS_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/8682f6f4a960643f4a99dbc25f4720477d9cdc616952bfdd803802882d0ac59b.jpeg";
const COACH_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/3976aeb23b436eb00874f307485c3ae60cd62df1811246b6dae6c43074a07491.jpeg";
const CORPORATE_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/ddbdea5f5dade86870487c8990a7224379cf7eded3e0b998d1c71ba5bb787d72.jpeg";
const WEDDING_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/ce6264487aeeff2fe6ae8ca8ceecbbdfc2821ed0cac804f19e67a5190acf5318.jpeg";
const AIRPORT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/6a6df88ae449d139ca19ad5ac57079f55ecb92ec705d827d9bbf37848c0dd2e8.jpeg";

const GALLERY = [
  { src: AIRPORT_IMAGE, caption: "Meet & greet arrivals" },
  { src: MINIBUS_IMAGE, caption: "Your crew on board" },
  { src: HENS_IMAGE, caption: "Hens winery days" },
  { src: COACH_IMAGE, caption: "Coaches up to 53 seats" },
  { src: WEDDING_IMAGE, caption: "Wedding days, handled" },
  { src: CORPORATE_IMAGE, caption: "Corporate days out" },
  { src: HERO_IMAGE, caption: "First-class chauffeurs" }
];

const TRUST_ITEMS = [
  "Fixed upfront pricing — no meters, no surge",
  "Professional licensed chauffeurs",
  "1 to 200+ guests via multi-vehicle convoys",
  "Door-to-door across South East Queensland",
  "Flight tracking on every airport pickup",
  "24/7, 365 days a year"
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState([]);
  const [tourPackages, setTourPackages] = useState([]);
  const [bookingForm, setBookingForm] = useState({ from: "", to: "", date: "", time: "" });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tours`).then((r) => setTourPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingForm((prev) => ({ ...prev, date: tomorrow.toISOString().split("T")[0], time: "10:00" }));
  }, []);

  useEffect(() => {
    const fetchFleet = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/vehicles`);
        const vehicles = response.data.filter((v) => v.is_active !== false).map((v) => ({
          type: v.name,
          capacity: `${v.capacity} passengers`,
          price: `From $${v.base_price}`,
          image: v.image_url || getDefaultVehicleImage(v.slug)
        }));
        setFleet(vehicles.length > 0 ? vehicles : getDefaultFleet());
      } catch (error) {
        setFleet(getDefaultFleet());
      }
    };
    fetchFleet();
  }, []);

  const getDefaultVehicleImage = (slug) => {
    const images = {
      sedan: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2017.png",
      suv: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/audi_q7-1.png",
      van: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/mercedes-benz-v-250-1-1.png",
      minibus: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png",
      luxury: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2017.png"
    };
    return images[slug] || images.sedan;
  };

  const getDefaultFleet = () => [
    { type: "Executive Sedan", capacity: "3 passengers", price: "From $90.00", image: "https://static.prod-images.emergentagent.com/jobs/3df45eb8-a170-4bc3-94d8-5c0848dd3464/images/1cc2782b93c210107b385c1a883c8401cca8a6d6c45065eaec21b3fb5ea0c91f.png" },
    { type: "First Class Sedan", capacity: "3 passengers", price: "From $130", image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2017.png" },
    { type: "Luxury SUV", capacity: "4 passengers", price: "From $105", image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/audi_q7-1.png" },
    { type: "Luxury People Mover", capacity: "6 passengers", price: "From $125", image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/mercedes-benz-v-250-1-1.png" },
    { type: "Mercedes Sprinter 11 Seater", capacity: "11 passengers", price: "From $165.00", image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png" },
    { type: "Mercedes Sprinter 15 Seater", capacity: "15 passengers", price: "From $220.00", image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png" }
  ];

  const swapLocations = () => setBookingForm((prev) => ({ ...prev, from: prev.to, to: prev.from }));

  const handleBookNow = () => {
    const params = new URLSearchParams();
    if (bookingForm.from) params.set("pickup", bookingForm.from);
    if (bookingForm.to) params.set("dropoff", bookingForm.to);
    if (bookingForm.date) params.set("date", bookingForm.date);
    if (bookingForm.time) params.set("time", bookingForm.time);
    navigate(`/book?${params.toString()}`);
  };

  const handleWhatsAppQuote = () => {
    let message = `*Quote Request from LimoNearMe*\n\n`;
    if (bookingForm.from) message += `*Pickup:* ${bookingForm.from}\n`;
    if (bookingForm.to) message += `*Drop-off:* ${bookingForm.to}\n`;
    if (bookingForm.date) message += `*Date:* ${bookingForm.date}\n`;
    if (bookingForm.time) message += `*Time:* ${bookingForm.time}\n`;
    if (!bookingForm.from && !bookingForm.to) message = `Hi! I'd like to get a quote for a transfer. Can you help me?`;
    window.open(`https://wa.me/61481437772?text=${encodeURIComponent(message)}`, "_blank");
  };

  const airports = [
    { name: "Brisbane Airport", code: "BNE", slug: "brisbane", image: "https://images.unsplash.com/photo-1566734904496-9309bb1798ae?w=400&h=250&fit=crop" },
    { name: "Gold Coast Airport", code: "OOL", slug: "gold-coast", image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop" },
    { name: "Sydney Airport", code: "SYD", slug: "sydney", image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&h=250&fit=crop" },
    { name: "Melbourne Airport", code: "MEL", slug: "melbourne", image: "https://images.unsplash.com/photo-1514395462725-fb4566210144?w=400&h=250&fit=crop" },
    { name: "Cairns Airport", code: "CNS", slug: "cairns", image: "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=400&h=250&fit=crop" },
    { name: "Perth Airport", code: "PER", slug: "perth", image: "https://images.unsplash.com/photo-1559331709-adc470c83d72?w=400&h=250&fit=crop" }
  ];

  const occasions = [
    { icon: Plane, title: "Airport Transfers", desc: "Meet-and-greet arrivals, flight tracking and fixed prices to every major Australian airport.", path: "/services/airport-transfers", color: "bg-sky-100 text-sky-700" },
    { icon: Users, title: "Minibus Charter", desc: "Mercedes Sprinters up to 15 seats with driver — groups, tours and events, everyone together.", path: "/services/minibus-charter", color: "bg-amber-100 text-amber-700" },
    { icon: Heart, title: "Weddings", desc: "Bridal party cars and guest shuttles that keep the whole day on time and stress-free.", path: "/services/weddings", color: "bg-rose-100 text-rose-700" },
    { icon: PartyPopper, title: "Hens & Bucks", desc: "Winery tours, brewery crawls and nights out — the party starts on the bus.", path: "/services/hens-bucks", color: "bg-purple-100 text-purple-700" },
    { icon: Briefcase, title: "Corporate Travel", desc: "Executive cars, corporate accounts and monthly invoicing for business travel.", path: "/corporate-travel", color: "bg-slate-100 text-slate-700" },
    { icon: Anchor, title: "Cruise Terminal", desc: "Direct transfers to the Brisbane International Cruise Terminal with all your luggage.", path: "/services/cruise-terminal", color: "bg-teal-100 text-teal-700" }
  ];

  const steps = [
    { num: "1", title: "Tell us your trip", desc: "Pickup, drop-off, date and time — book online in 60 seconds or message us on WhatsApp." },
    { num: "2", title: "Get a fixed price", desc: "Instant, all-inclusive pricing. No meters, no surge, no surprises." },
    { num: "3", title: "We confirm everything", desc: "Driver, vehicle and timing locked in, with confirmation sent to your email and phone." },
    { num: "4", title: "Relax and ride", desc: "Your chauffeur arrives early, tracks your flight and gets you there in comfort." }
  ];

  const whyChooseUs = [
    { icon: Shield, title: "Safe & Reliable", desc: "Professional licensed chauffeurs" },
    { icon: Clock, title: "24/7 Service", desc: "Available round the clock" },
    { icon: Plane, title: "Flight Tracking", desc: "Real-time flight monitoring" },
    { icon: CreditCard, title: "Fixed Pricing", desc: "Quoted upfront, no surprises" },
    { icon: Star, title: "5-Star Rated", desc: "Trusted by thousands" },
    { icon: Users, title: "1 to 200+ Guests", desc: "Sedans, coaches & convoys" }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.limonearme.com.au/#organization",
        name: "Limo Near Me",
        url: "https://www.limonearme.com.au",
        logo: "https://www.limonearme.com.au/limo-logo.jpg",
        contactPoint: { "@type": "ContactPoint", telephone: "+61481437772", contactType: "customer service", areaServed: "AU", availableLanguage: "English" }
      },
      {
        "@type": "WebSite",
        "@id": "https://www.limonearme.com.au/#website",
        url: "https://www.limonearme.com.au",
        name: "Limo Near Me - Airport Transfers & Minibus Charter Australia",
        publisher: { "@id": "https://www.limonearme.com.au/#organization" }
      },
      {
        "@type": "LocalBusiness",
        name: "Limo Near Me",
        image: "https://www.limonearme.com.au/limo-logo.jpg",
        address: { "@type": "PostalAddress", addressCountry: "AU" },
        telephone: "+61481437772",
        email: "info@limonearme.com.au",
        url: "https://www.limonearme.com.au",
        priceRange: "$$-$$$",
        openingHours: "Mo-Su 00:00-23:59"
      },
      {
        "@type": "Service",
        serviceType: "Airport Transfer",
        provider: { "@id": "https://www.limonearme.com.au/#organization" },
        areaServed: cityLinks.map((c) => ({ "@type": "City", name: c.name }))
      }
    ]
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Limo Near Me | Airport Transfers, Chauffeur Cars & Minibus Charter Australia</title>
          <meta name="description" content="Limo hire near you — private airport transfers, luxury chauffeur cars and Mercedes Sprinter minibus charters across Brisbane, Gold Coast, Sydney, Melbourne, Cairns & Perth. Fixed prices, professional drivers, book online 24/7." />
          <meta name="keywords" content="limo near me, limo hire, airport transfers Brisbane, airport transfers Gold Coast, chauffeur Sydney, chauffeur Melbourne, minibus charter, Mercedes Sprinter hire, wedding car hire, corporate travel" />
          <link rel="canonical" href="https://www.limonearme.com.au" />
          <meta property="og:title" content="Limo Near Me | Airport Transfers & Minibus Charter Australia" />
          <meta property="og:description" content="Private airport transfers, chauffeur cars and minibus charters with fixed prices, Australia-wide. Book online 24/7." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.limonearme.com.au" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />
        <PublicHeader />

        {/* Hero */}
        <section className="relative pt-24 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMAGE}')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/80" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 py-16 md:py-24">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/20 text-amber-300 mb-6 backdrop-blur-sm">
                <Star className="h-4 w-4 fill-amber-300" />
                <span className="text-sm font-medium">Australia-wide · Fixed prices · 24/7</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
                Airport Transfers, Chauffeurs<br className="hidden md:block" />
                <span className="text-amber-400"> & Minibus Charters</span>
              </h1>
              <p className="text-lg text-gray-200 max-w-2xl mx-auto">
                Private, chauffeur-driven transport across Brisbane, the Gold Coast, Sydney, Melbourne and beyond —
                from executive sedans to 53-seat luxury coaches. No group too big, no trip too small. Book in 60 seconds.
              </p>
            </motion.div>

            {/* Booking Form Card */}
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">From</label>
                  <AddressAutocomplete
                    value={bookingForm.from}
                    onChange={(value) => setBookingForm((prev) => ({ ...prev, from: value }))}
                    placeholder="Enter an airport name, code, or address..."
                    icon={Building2}
                    className="h-12 text-base bg-gray-50 border-black/10 focus:bg-white"
                    data-testid="from-input"
                  />
                </div>
                <div className="flex justify-center -my-1">
                  <button onClick={swapLocations} className="p-2 rounded-full bg-amber-50 hover:bg-amber-100 transition-colors" data-testid="swap-btn">
                    <ArrowLeftRight className="h-4 w-4 text-amber-700 rotate-90" />
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">To</label>
                  <AddressAutocomplete
                    value={bookingForm.to}
                    onChange={(value) => setBookingForm((prev) => ({ ...prev, to: value }))}
                    placeholder="Enter an airport name, code, or address..."
                    icon={MapPin}
                    className="h-12 text-base bg-gray-50 border-black/10 focus:bg-white"
                    data-testid="to-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Travel Date</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input type="date" value={bookingForm.date} onChange={(e) => setBookingForm((prev) => ({ ...prev, date: e.target.value }))} className="pl-11 h-12 text-base bg-gray-50 border-black/10 focus:bg-white" data-testid="date-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Pickup Time</label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input type="time" value={bookingForm.time} onChange={(e) => setBookingForm((prev) => ({ ...prev, time: e.target.value }))} className="pl-11 h-12 text-base bg-gray-50 border-black/10 focus:bg-white" data-testid="time-input" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button onClick={handleBookNow} className="h-14 text-lg font-semibold rounded-xl" data-testid="hero-book-now-btn">
                    <Search className="h-5 w-5 mr-2" /> Book Now
                  </Button>
                  <Button onClick={handleWhatsAppQuote} className="h-14 text-lg font-semibold rounded-xl bg-green-600 hover:bg-green-700 text-white" data-testid="hero-whatsapp-btn">
                    <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp Quote
                  </Button>
                </div>
              </div>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6 mt-8">
              {[
                { icon: Shield, text: "Safe & Secure" },
                { icon: Clock, text: "24/7 Service" },
                { icon: Star, text: "5-Star Rated" },
                { icon: CreditCard, text: "Fixed Prices" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-200">
                  <item.icon className="h-4 w-4 text-amber-400" />
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery marquee */}
        <section className="py-10 bg-white overflow-hidden">
          <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-amber-700 mb-6">The fleet, the days out, the crew</p>
          <div className="flex animate-marquee w-max" data-testid="gallery-marquee">
            {[...GALLERY, ...GALLERY].map((g, i) => (
              <div key={i} className="relative w-72 h-48 mx-2 rounded-xl overflow-hidden flex-shrink-0 group">
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-2 left-3 text-white text-sm font-semibold">{g.caption}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Trust ticker */}
        <section className="py-4 bg-gray-950 overflow-hidden border-y border-amber-500/20">
          <div className="flex animate-marquee w-max items-center">
            {[...TRUST_ITEMS, ...TRUST_ITEMS, ...TRUST_ITEMS].map((t, i) => (
              <span key={i} className="flex items-center text-sm text-gray-300 whitespace-nowrap">
                <span className="mx-6 text-amber-400">✦</span>{t}
              </span>
            ))}
          </div>
        </section>

        {/* Occasions */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Every trip, <span className="font-accent text-amber-700">handled.</span></h2>
              <p className="text-muted-foreground">Whatever gets you moving, we have a vehicle and a plan for it</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {occasions.map((o, index) => (
                <motion.div key={o.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}>
                  <Link to={o.path} className="block bg-white rounded-2xl border border-black/5 p-6 hover:shadow-xl hover:border-amber-300 transition-all h-full" data-testid={`occasion-card-${index}`}>
                    <div className={`w-12 h-12 rounded-xl ${o.color} flex items-center justify-center mb-4`}>
                      <o.icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-bold text-foreground mb-2">{o.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{o.desc}</p>
                    <span className="text-amber-700 text-sm font-semibold inline-flex items-center">Explore <ChevronRight className="h-4 w-4 ml-1" /></span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Two paths: Chauffeur vs Minibus */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Choose your <span className="font-accent text-amber-700">path.</span></h2>
              <p className="text-muted-foreground">Two ways to ride — both with a professional driver and a fixed price</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative rounded-2xl overflow-hidden group min-h-[380px] flex items-end">
                <img src={HERO_IMAGE} alt="Private chauffeur car with driver opening door" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="relative z-10 p-8">
                  <p className="text-amber-400 text-sm font-semibold mb-2">1 – 6 PASSENGERS</p>
                  <h3 className="text-2xl font-bold text-white mb-3">Private Chauffeur Transfers</h3>
                  <p className="text-gray-300 text-sm mb-5 max-w-md">Executive sedans, luxury SUVs and V-Class people movers for airport runs, corporate travel and special occasions.</p>
                  <Link to="/book"><Button className="rounded-full px-6" data-testid="path-chauffeur-btn">Book a Chauffeur <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative rounded-2xl overflow-hidden group min-h-[380px] flex items-end">
                <img src={MINIBUS_IMAGE} alt="Group boarding a Mercedes Sprinter minibus charter" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="relative z-10 p-8">
                  <p className="text-amber-400 text-sm font-semibold mb-2">7 – 200+ PASSENGERS</p>
                  <h3 className="text-2xl font-bold text-white mb-3">Minibus & Coach Charter</h3>
                  <p className="text-gray-300 text-sm mb-5 max-w-md">Mercedes Sprinters, 21-seat Coasters and 53-seat luxury coaches — plus multi-vehicle convoys so groups of any size travel together.</p>
                  <Link to="/services/minibus-charter"><Button className="rounded-full px-6" data-testid="path-minibus-btn">Explore Minibus Charter <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Booked in four <span className="font-accent text-amber-700">easy steps.</span></h2>
              <p className="text-muted-foreground">From first click to front door — we make the whole thing effortless</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {steps.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-amber-500 text-white text-xl font-black flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">{s.num}</div>
                  <h3 className="font-bold text-foreground mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Airport Locations */}
        <section id="airports" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Airport Transfer Locations</h2>
              <p className="text-muted-foreground">Private chauffeur pickups at every major Australian airport</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {airports.map((airport, index) => (
                <Link key={airport.code} to={`/chauffeur/${airport.slug}`} className="group">
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="relative rounded-xl overflow-hidden aspect-[4/3] bg-gray-100">
                    <img src={airport.image} alt={`${airport.name} private transfers`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white font-semibold text-sm">{airport.name}</p>
                      <p className="text-amber-300 text-xs">({airport.code})</p>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section className="py-14 bg-gray-950 text-white">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-testid="stats-band">
            {[
              { num: "1–200+", label: "Guests per booking" },
              { num: "24/7", label: "Every day of the year" },
              { num: "60 sec", label: "To book online" },
              { num: "6", label: "Cities serviced" }
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <p className="text-4xl md:text-5xl font-black text-amber-400 mb-2">{s.num}</p>
                <p className="text-sm text-gray-400">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Why Choose Limo Near Me?</h2>
              <p className="text-muted-foreground">Trusted by travellers, families and businesses across Australia</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {whyChooseUs.map((item, index) => (
                <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <item.icon className="h-6 w-6 text-amber-700" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Fleet */}
        <section id="fleet" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Modern luxury, <span className="font-accent text-amber-700">every ride.</span></h2>
              <p className="text-muted-foreground">From executive sedans to 53-seat luxury coaches — and multi-vehicle convoys for 200+ guests</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {fleet.map((vehicle, index) => (
                <motion.div key={vehicle.type} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="bg-white rounded-xl border border-black/5 overflow-hidden hover:shadow-lg hover:border-amber-300 transition-all">
                  <div className="h-40 bg-gradient-to-br from-amber-50 to-gray-50 flex items-center justify-center p-4">
                    <img src={vehicle.image} alt={vehicle.type} className="max-h-full object-contain" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground">{vehicle.type}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{vehicle.capacity}</p>
                    <div className="flex items-center justify-end">
                      <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Book</Button></Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-gray-950 text-white rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="convoy-card">
              <div>
                <h3 className="text-xl font-bold mb-1">Bigger group? <span className="font-accent text-amber-400">No problem.</span></h3>
                <p className="text-gray-400 text-sm max-w-xl">Over 53 guests, we run multi-vehicle convoys from our fleet — 100, 200 or more, everyone travels together on the same schedule, with one booking and one invoice.</p>
              </div>
              <a href="https://wa.me/61481437772?text=Hi!%20I%20need%20transport%20for%20a%20large%20group." target="_blank" rel="noreferrer" className="flex-shrink-0">
                <Button className="rounded-full px-8 font-bold">Plan a Large Group Move</Button>
              </a>
            </div>
          </div>
        </section>
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src={HENS_IMAGE} alt="Hens party group with luxury minibus at a winery" className="rounded-2xl shadow-2xl w-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-amber-700 font-semibold text-sm mb-2">GOOD VIBES ONLY</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Winery tours, hens & bucks and <span className="font-accent text-amber-700">big days out.</span></h2>
              <p className="text-muted-foreground mb-6">
                Mount Tamborine cellar doors, Scenic Rim lavender farms, Brisbane brewery crawls and Gold Coast nights out —
                our party-ready Sprinter minibuses come with Bluetooth for your playlist and a professional driver so the
                whole crew celebrates together.
              </p>
              <ul className="space-y-3 mb-8">
                {["Private minibus for up to 15 with driver", "Custom itineraries — we know the best stops", "Safe late-night returns, door to door"].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/hens-bucks">
                <Button size="lg" className="rounded-full px-8" data-testid="hens-feature-btn">Plan Your Party <ChevronRight className="ml-2 h-5 w-5" /></Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Tour packages */}
        <section className="py-16 bg-gray-950 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-amber-400 mb-3">All-inclusive experiences</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">Pick a day out, <span className="font-accent text-amber-400">we'll do the rest.</span></h2>
              <p className="text-gray-400">Hosted packages with the bus, driver and run sheet already sorted</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="tours-section">
              {tourPackages.map((t, i) => (
                <motion.div key={t.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <Link to={`/tours/${t.slug}`} className="block group" data-testid={`home-tour-${t.slug}`}>
                    <div className="rounded-xl overflow-hidden aspect-[4/5] relative mb-2">
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 flex flex-col justify-end min-h-[4.5rem]">
                        <p className="text-white font-bold text-sm leading-tight [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">{t.name}</p>
                        <p className="text-amber-400 text-xs font-semibold mt-1">From ${t.fromPrice}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/tours">
                <Button size="lg" className="rounded-full px-10 font-bold" data-testid="view-all-tours-btn">Browse Tours & Packages <ChevronRight className="ml-2 h-5 w-5" /></Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Areas we service */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Where we <span className="font-accent text-amber-700">go.</span></h2>
              <p className="text-muted-foreground">Door-to-door pickups across South East Queensland and Australia's major cities</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {cityLinks.map((c) => (
                <Link key={c.path} to={c.path} className="px-5 py-2.5 rounded-full bg-gray-950 text-white text-sm font-semibold hover:bg-amber-600 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {locations.map((l) => (
                <Link key={l.slug} to={`/limo-hire/${l.slug}`} className="px-4 py-2 rounded-full bg-white border border-black/10 text-muted-foreground text-sm hover:border-amber-400 hover:text-amber-700 transition-all" data-testid={`area-link-${l.slug}`}>
                  {l.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SEO text block */}
        <section className="py-14 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">Australia's Friendly Chauffeur & Charter Company</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Limo Near Me is a private chauffeur, airport transfer and minibus charter company running a modern luxury
              fleet across <strong>Brisbane, the Gold Coast, the Sunshine Coast, Sydney, Melbourne, Cairns and Perth</strong>.
              Book a chauffeur-driven sedan, SUV or people mover on its own, or charter a Mercedes Sprinter minibus with
              driver for weddings, winery tours, cruise terminal transfers, corporate events and hens or bucks parties.
              Every booking comes with a fixed upfront price, flight tracking on airport pickups, and a professional,
              locally-based driver — 24 hours a day, 7 days a week.
            </p>
          </div>
        </section>

        {/* Book direct */}
        <section className="py-16 bg-amber-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-amber-700 mb-3">Why book direct</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Book direct with the operator. <span className="font-accent text-amber-700">Better price, better day.</span></h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="book-direct-section">
              {[
                { title: "Sharper Pricing", desc: "Operator-direct pricing. The real number, everything included, quoted upfront before you book." },
                { title: "Direct Contact", desc: "Call or WhatsApp and you reach the team running your booking — not a call centre." },
                { title: "Real Flexibility", desc: "Plans change. We adjust pickups, timings and stops on the day and keep it easy." },
                { title: "We Actually Care", desc: "Your day matters to us as much as it does to you — every driver, every trip, every time." }
              ].map((v, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl p-6 border border-amber-200/60">
                  <div className="w-8 h-1 bg-amber-500 rounded-full mb-4" />
                  <h3 className="font-bold mb-2">{v.title}</h3>
                  <p className="text-sm text-muted-foreground">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gray-950 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Book Your Transfer?</h2>
            <p className="text-gray-400 mb-8">Get an instant quote online or chat with us on WhatsApp</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="rounded-full px-8 font-bold" data-testid="cta-book-online-btn">Book Online</Button>
              </Link>
              <Button size="lg" onClick={handleWhatsAppQuote} className="rounded-full px-8 bg-green-600 hover:bg-green-700 text-white" data-testid="cta-whatsapp-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> Get Quote on WhatsApp
              </Button>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </section>

        <SeoFooter />

        {/* Sticky mobile action bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-950/95 backdrop-blur-md border-t border-amber-500/30 p-3 flex gap-3" data-testid="mobile-sticky-bar">
          <Link to="/book" className="flex-1">
            <Button className="w-full rounded-full font-bold">Book Now</Button>
          </Link>
          <a href="https://wa.me/61481437772" target="_blank" rel="noreferrer" className="flex-1">
            <Button className="w-full rounded-full bg-green-600 hover:bg-green-700 text-white">
              <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </PageTransition>
  );
};

export default LandingPage;
